<template>

  <v-container fluid class="sas-container pa-0">

      <!-- HEADER -->
      <div class="sas-header d-flex align-center">

        <!-- SEARCH INPUT -->
        <div class="sas-search-bar d-flex align-center" :class="{'sas-error': shouldDisplayError}">
          <v-icon>mdi-magnify</v-icon>

          <v-text-field
              v-model="searchData.query"
              class=""
              dense
              hide-details
              solo
              flat
              placeholder="Rechercher une agence..."
              @input="onSearchInput"
          />

          <div v-if="searchData.loading" class="sas-end-slot d-flex align-center justify-center">
            <v-progress-circular indeterminate size="18" width="2" />
          </div>

          <div v-if="searchData.query" class="sas-end-slot d-flex align-center justify-center cursor-pointer" @click.stop="clearInput">
            <v-icon>mdi-close</v-icon>
          </div>
          <div v-if="shouldDisplayError">
            <p class="text-caption danger">Agence requise</p>
          </div>
      </div>
      </div>
      <!-- HEADER [END] -->


      <!-- CONTENT -->
      <div class="sas-content">
        <!-- [CONTENT] Search Live Results -->
        <div class="sas-results" v-if="canRunSearch && !searchData.loading && !searchData.error">
          <SmartAgencySearchResult
              :agencies="searchData.results.agencies"
              @select-agency="onSelectAgency">

            <template #no-result-action>
              <div @click="onNewAgency">
                <v-chip small class="cursor-pointer">
                  <v-icon small color="grey darken-1">mdi-plus</v-icon>
                  <span class="font-weight-medium grey--text text--darken-2">Nouvelle Agence</span>
                </v-chip>
              </div>
            </template>

          </SmartAgencySearchResult>

        </div>
      </div>
      <!-- CONTENT [END] -->


      <smart-dialog :width="1000" title="Nouvelle agence" v-model="agencyDialog.show" transition="dialog-bottom-transition">
        <crm-agency-info no-redirect-after-create @created="newAgencyCreated" :name="searchData.query"/>
      </smart-dialog>

    </v-container>
</template>


<script>
import debounce from "lodash.debounce";
import { get } from "@/utils/api";
import FormMixin from "@/components/mixins/FormMixin";
import CrmAgencyInfo from "@/components/commons/CrmAgencyInfo.vue";
import SmartAgencySearchResult from "@/views/hlagons/workshop/SmartAgencySearchResult.vue";

export default {
  name: "SmartAgencySearch",
  mixins: [FormMixin],
  components: {SmartAgencySearchResult, CrmAgencyInfo},
  props: {
    showIdleAsError: {type: Boolean, default: false},
    canCreate: {type: Boolean, default: false},
    sendInput: {type: [String, null], default: null}
  },
  emits: ["select", "input-received"],
  data: () => ({
    requestKey: 0,
    debouncedFetch: null,
    searchData: {
      query: "",
      scope: "agency",
      loading: false,
      error: null,
      results: {
        agencies: [],
      },
    },
    agencyDialog: {
      show: false
    }
  }),
  created() {
    this.debouncedFetch = debounce(() => {
      this.fetchResults();
    }, 500);
  },
  beforeDestroy() {
    if (this.debouncedFetch && this.debouncedFetch.cancel) this.debouncedFetch.cancel();
  },
  watch: {
    sendInput(newInput) {
      if (newInput) {
        this.clearInput()
        this.searchData.query = newInput
        this.$emit('input-received')
      }
    }
  },
  computed: {
    canRunSearch() {
      const parts = this.searchData.query
          .split(/\s+/)
          .map(t => t.trim())
          .filter(t => t.length > 0)

      const isValidToken = (t) => {
        return (t.length >= 3 || (t.length >= 2 && /^[0-9]+$/.test(t)))
      }
      return parts.some(isValidToken)
    },
    shouldDisplayError() {
      return this.showIdleAsError && this.searchData.query.length === 0
    }
  },
  methods: {
    onSearchInput() {
      if (!this.canRunSearch) {
        this.searchData.results = this.emptyResult();
        this.searchData.error = null;
        if (this.debouncedFetch.cancel) this.debouncedFetch.cancel();
        return;
      }
      this.debouncedFetch();
    },
    onSelectAgency(agency) {
      this.$emit('select', agency)
    },
    clearInput() {
      this.searchData.query = "";
      this.searchData.results = this.emptyResult();
      this.searchData.error = null;
      this.searchData.loading = false;
      if (this.debouncedFetch.cancel) this.debouncedFetch.cancel();
    },
    fetchResults() {
      if (!this.canRunSearch) return;

      const key = ++this.requestKey;
      this.searchData.loading = true;
      this.searchData.error = null;

      const url = `/api/workshop/search?q=${encodeURIComponent(this.searchData.query)}&scope=${encodeURIComponent(this.scope)}`;

      get(url).json((result) => {
        if (key !== this.requestKey) return;
        this.searchData.results = {
          agents: Array.isArray(result.agents) ? result.agents : [],
          agencies: Array.isArray(result.agencies) ? result.agencies : [],
        }
        this.searchData.loading = false;
      }).catch(() => {
        this.searchData.error = true // [N] Not handled further yet
      })
    },
    emptyResult() {
      return {
        agencies: [],
      }
    },
    onNewAgency() {
      this.agencyDialog.show = true
    },
    newAgencyCreated(agency) {
      this.agencyDialog.show = false
      this.onSelectAgency(agency)
    }
  }
}
</script>


<style scoped>
.cursor-pointer {
  cursor: pointer;
}

.sas-container {
  width: 100%;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.sas-header {
  flex: 0 0 auto;
  gap: 12px;
  min-width: 0;
  max-width: 600px;
}

.sas-search-bar {
  flex: 1;
  gap: 12px;
  height: 52px;
  padding: 0 16px;
  border: 1px solid #dadce0;
  border-radius: 26px;
  background: white;
  transition: all 0.2s ease;
}

.sas-search-bar:focus-within {
  border-color: #1976d2;
  box-shadow: 0 0 0 2px rgba(25, 118, 210, 0.15);
}

.sas-search-bar.sas-error {
  border-color: #ff5252;
}

.sas-search-bar.sas-error:focus-within {
  box-shadow: 0 0 0 2px rgba(255, 82, 82, 0.25);
}

.sas-end-slot {
  width: 24px;
  height: 24px;
  flex: 0 0 auto;
}

.sas-content {
  flex: 1 1 auto;
  min-height: 0;
  width: 100%;
  overflow-y: auto;
}

.sas-results {
  margin-top: 24px;
  width: 100%;
  font-size: x-small;
  color: #aeaeae;
}

.sas-results {
  width: 100%;
}

.danger {
  color: #ff5252;
}

</style>